import { default as store } from "@/store/index";
import Ship from "@/store/model/ship";
import Defense from "@/store/model/defense";
import Research from "@/store/model/research";
import ResourceValue from "@/store/model/resource-value";
import { BattleReportImporter } from "@/services/battle-report-importer";
import SpecialShipData from "@/store/model/special-ship-data";
import { ResearchId } from "@/helpers/research-id";
import Fleet from "@/store/model/fleet";
import Specialist from "@/store/model/specialist";
import { ResourceTypeId } from "@/helpers/resource-type-id";

const SpaceportFleetPattern = /Typ[ \t]+Speed[ \t]+Piloten[ \t]+Anzahl[ \t]+Auswahl\n((?:[ \t]*Ship[ \t]+.*\n.*\n(?:Alle {3}Keine\n)?)+)/;
const SpaceportEntryPattern = /[ \t]*Ship[ \t]+(.*)[ \t]+[0-9`']+[ \t]+AE\/h[ \t]+[0-9`]+[ \t]+([0-9`]+)\n.*\n?(?:Alle {3}Keine\n)?/;

const InfosFleetPattern = /Typ[ \t]+Anzahl[ \t]+(?:Eingebunkert|Felder)\n((?:.*\n)+)/;
const InfosEntryPattern = /(.*)[ \t]+([0-9`]+)[ \t]+[0-9`]+\n/;

const EspionageReportPattern = /Planet: ([0-9]+\/[0-9]+ : [0-9]+\/[0-9]+ : [0-9]+) (.*)\nBesitzer: (.*)\n\n(?:GEBÄUDE:\n((?:.*[ \t]+[0-9`]+\n)+|Nichts vorhanden!\n)\n)?(?:TECHNOLOGIEN:\n((?:.*[ \t]+[0-9`]+\n)+|Nichts vorhanden!\n)\n)?(?:SCHIFFE:\n((?:.*[ \t]+[0-9`]+\n)+|Nichts vorhanden!\n)\n)?(?:VERTEIDIGUNG:\n((?:.*[ \t]+[0-9`]+\n)+|Nichts vorhanden!\n)\n)?(?:ROHSTOFFE:\n((?:.*[ \t]+[0-9`]+\n)+|Nichts vorhanden!\n)\n)?/;
const EspionageEntryPattern = /(.*)[ \t]+([0-9`]+)\n/;

const ResearchOverviewPattern = /Kriegstechnologien[ \t\n]*((?:.*[ \t\n]+?[0-9]+[ \t\n]*)+)/;
const ResearchEntryPattern = /(.*)[ \t\n]+?([0-9]+)/;

const IncomingFleetPattern = /Schiffe:[ \t]+((?:,?[ \t]*[0-9]+[ \t]+.*)+)/;
const IncomingFleetEntryPattern = /([0-9]+)[ \t]+(.*?)(?:,|$)/;

const VISIBLE_RESEARCH_TYPES = [
  ResearchId.WEAPONS,
  ResearchId.ARMOR,
  ResearchId.SHIELDS,
  ResearchId.REGENERATION
];

const FleetImporter = {
  import(data, role) {
    let json = null;
    try {
      json = JSON.parse(data);
    } catch (ignored) {
      //no catch required
    }
    if (json != null) {
      return FleetImporter.importJson(json);
    }
    const fleetMatch = data.match(SpaceportFleetPattern);
    if (fleetMatch) {
      return {
        items: FleetImporter.importData(fleetMatch[1], SpaceportEntryPattern)
      };
    }
    const infosMatch = data.match(InfosFleetPattern);
    if (infosMatch) {
      return {
        items: FleetImporter.importResearch(data).concat(
          FleetImporter.importData(infosMatch[1], InfosEntryPattern)
        )
      };
    }
    const espionageMatch = data.match(EspionageReportPattern);
    if (espionageMatch) {
      return {
        planetCoordinates: espionageMatch[1],
        planetName: espionageMatch[2],
        playerName: espionageMatch[3],
        items: FleetImporter.importData(
          (espionageMatch[5] || "") +
            (espionageMatch[6] || "") +
            (espionageMatch[7] || ""),
          EspionageEntryPattern
        )
      };
    }
    const researchOverviewMatch = data.match(ResearchOverviewPattern);
    if (researchOverviewMatch) {
      return {
        items: FleetImporter.importData(
          researchOverviewMatch[1],
          ResearchEntryPattern
        ).sort((a, b) => {
          const indexA = VISIBLE_RESEARCH_TYPES.indexOf(a.typeId);
          const indexB = VISIBLE_RESEARCH_TYPES.indexOf(b.typeId);
          return indexA > indexB ? 1 : indexA < indexB ? -1 : 0;
        })
      };
    }
    const incomingFleetMatch = data.match(IncomingFleetPattern);
    if (incomingFleetMatch) {
      return {
        items: FleetImporter.importData(
          incomingFleetMatch[1],
          IncomingFleetEntryPattern
        )
      };
    }

    return BattleReportImporter.import(data, role);
  },
  importResearch: data => {
    const result = [];
    const researchTypes = store.getters["data/researchTypes"];
    VISIBLE_RESEARCH_TYPES.forEach(typeId => {
      const researchType = researchTypes[typeId];
      const pattern = new RegExp(`${researchType.name}[ \t]+([0-9]+)`);
      const match = pattern.exec(data);
      if (!match) {
        return;
      }
      const level = parseInt(match[1]);
      result.push(new Research(typeId, level));
    });
    return result;
  },
  importData: (data, entryPattern) => {
    let entryMatches = [];
    let entryMatch;
    while ((entryMatch = entryPattern.exec(data)) !== null) {
      entryMatches.push(entryMatch);
      data = data.substring(entryMatch.index + entryMatch[0].length);
    }

    let shipTypes = Object.values(store.getters["data/shipTypes"]);
    let defenseTypes = Object.values(store.getters["data/defenseTypes"]);
    let researchTypes = Object.values(store.getters["data/researchTypes"]);
    let resourceTypes = Object.values(store.getters["data/resourceTypes"]);
    let result = [];
    for (let entryData of entryMatches) {
      const amountIndex = Number.isNaN(parseInt(entryData[2])) ? 1 : 2;
      const nameIndex = amountIndex === 1 ? 2 : 1;
      let name = entryData[nameIndex].toLowerCase().trim();
      let amount = parseInt(
        entryData[amountIndex]
          .split("`")
          .join("")
          .trim()
      );
      let shipType = shipTypes
        .filter(t => t.name.toLowerCase() === name)
        .shift();
      if (!shipType) {
        let defenseType = defenseTypes
          .filter(t => t.name.toLowerCase() === name)
          .shift();
        if (!defenseType) {
          let researchType = researchTypes
            .filter(t => t.name.toLowerCase() === name)
            .shift();
          if (!researchType) {
            let resourceType = resourceTypes
              .filter(t => t.name.toLowerCase() === name)
              .shift();
            if (!resourceType) {
              console.warn(
                "Unknown object with name " + name + " encountered."
              );
              continue;
            }
            result.push(new ResourceValue(resourceType.key, amount));
            continue;
          }
          result.push(new Research(researchType.key, amount));
          continue;
        }
        result.push(new Defense(defenseType.key, amount));
        continue;
      }
      result.push(new Ship(shipType.key, amount));
    }
    return result;
  },
  importJson(json) {
    const items = [];
    const subfleets = [];
    if (json.research != null) {
      json.research.forEach(i => items.push(new Research(i.typeId, i.level)));
    }
    if (json.ships != null) {
      json.ships.forEach(i =>
        items.push(
          new Ship(
            i.typeId,
            i.amount,
            i.special != null ? SpecialShipData.deserialize(i.special) : null
          )
        )
      );
    }
    if (json.defenses != null) {
      json.defenses.forEach(i => items.push(new Defense(i.typeId, i.amount)));
    }
    if (json.subfleets != null) {
      json.subfleets
        .map(f => this.importJson(f))
        .forEach(f => subfleets.push(f));
    }
    return {
      playerName: json.name,
      planetName: json.planetName,
      planetCoordinates: json.planetCoordinates,
      items: items,
      subfleets: subfleets
    };
  },
  apply(fleet, data, role) {
    if (data.playerName) {
      fleet.name = data.playerName;
    }
    if (data.planetName) {
      fleet.planetName = data.planetName;
    }
    if (data.planetCoordinates) {
      fleet.planetCoordinates = data.planetCoordinates;
    }
    const ships = data.items.filter(i => i instanceof Ship);
    const defenses = data.items.filter(i => i instanceof Defense);
    const research = data.items.filter(i => i instanceof Research);
    const specialists = data.items.filter(i => i instanceof Specialist);
    for (const item of research) {
      if (VISIBLE_RESEARCH_TYPES.indexOf(item.typeId) < 0) {
        continue;
      }
      const existing = fleet.research.find(r => r.typeId === item.typeId);
      if (existing) {
        existing.level = item.level;
        continue;
      }
      fleet.research.push(item);
    }
    for (const item of ships) {
      const existing = fleet.ships.find(s => s.typeId === item.typeId);
      if (existing) {
        existing.amount = item.amount;
        continue;
      }
      fleet.ships.push(item);
    }
    for (const item of specialists) {
      const existing = fleet.specialists.find(s => s.typeId === item.typeId);
      if (existing) {
        continue;
      }
      fleet.specialists.push(item);
    }
    if (role === "defender") {
      for (const item of defenses) {
        const existing = fleet.defenses.find(d => d.typeId === item.typeId);
        if (existing) {
          existing.amount = item.amount;
          continue;
        }
        fleet.defenses.push(item);
      }
    }
    this.addDefaultTechs(fleet.research);
    if (data.subfleets != null) {
      fleet.subfleets.splice(0, fleet.subfleets.length);
      data.subfleets.forEach(f => {
        const subfleet = new Fleet();
        this.apply(subfleet, f, "ally");
        fleet.subfleets.push(subfleet);
      });
    }
  },
  addDefaultTechs(research) {
    const defaultTechs = [
      ResearchId.WEAPONS,
      ResearchId.ARMOR,
      ResearchId.SHIELDS,
      ResearchId.REGENERATION
    ];
    defaultTechs.forEach(t => {
      if (research.findIndex(r => r.typeId === t) >= 0) {
        return;
      }
      research.push(new Research(t, 0));
    });
  },
  addDebrisResourceTypes(resources) {
    const debrisTypes = [
      ResourceTypeId.METAL,
      ResourceTypeId.CRYSTAL,
      ResourceTypeId.PLASTIC
    ];
    debrisTypes.forEach(t => {
      if (resources.findIndex(r => r.typeId === t) >= 0) {
        return;
      }
      resources.push(new ResourceValue(t, 0));
    });
  }
};

export { FleetImporter };
